import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OperationResult, OperationResultWithValue } from '../../../shared/models/operation-result';
import {
  EipsesAeaServiceProvider,
  EipsesAeaVerification,
  EipsesSample,
  EipsesSurveyReport,
  EipsesSurveyReportFilters,
  ServiceProviderAddRequest,
  ServiceProviderSearchEntity,
} from '../models/eipses';
import { NotificationService } from '../../../shared/services/notification.service';
import { PaginatedList } from '../../../shared/models/paginated-list';

@Injectable({
  providedIn: 'root',
})
export class EipsesService {
  private baseUrl = 'api/eipses';
  constructor(
    private http: HttpClient,
    private readonly notificationService: NotificationService
  ) {}

  getEipsesServiceProviders(searchParams: any, pageNumber: number, pageSize: number, orderBy = 'lastname', orderByDescending = true) {
    let params = new HttpParams();

    Object.keys(searchParams).forEach((k) => {
      params = params.set(k, searchParams[k]);
    });

    params = params.set('pageNumber', pageNumber);
    params = params.set('pageSize', pageSize);
    params = params.set('orderByDescending', orderByDescending);
    params = params.set('orderBy', orderBy);
    return this.http.get<OperationResultWithValue<PaginatedList<EipsesAeaServiceProvider>>>(`${this.baseUrl}/providers`, { params });
  }

  getSampleBySchoolYear(schoolYearId: string) {
    return this.http.get<OperationResultWithValue<EipsesSample>>(`${this.baseUrl}/sample/${schoolYearId}`);
  }

  getLatestSample() {
    return this.http.get<OperationResultWithValue<EipsesSample>>(`${this.baseUrl}/latest-sample`);
  }

  resetAeaVerifications(schoolYearId: string) {
    return this.http.post<OperationResultWithValue<EipsesSample>>(`${this.baseUrl}/reset/${schoolYearId}`, {});
  }

  getAeaVerifications() {
    return this.http.get<OperationResultWithValue<EipsesAeaVerification[]>>(`${this.baseUrl}/aea-verifications`);
  }

  finalizeAeaVerification(id: string) {
    return this.http.put<OperationResult>(`${this.baseUrl}/finalize/${id}`, {});
  }

  getAeaVerification(aeaId: string) {
    return this.http.get<OperationResultWithValue<EipsesAeaVerification>>(`${this.baseUrl}/aea-verification/${aeaId}`);
  }

  searchEipsesServiceProviders(
    aeaId: string,
    keyword: string,
    professionId: string,
    pageNumber: number,
    pageSize: number,
    orderBy = 'lastname',
    orderByDescending = true
  ) {
    let params = new HttpParams();
    params = params.set('aeaId', aeaId);
    params = params.set('keyword', keyword);
    params = params.set('professionId', professionId);
    params = params.set('pageNumber', pageNumber);
    params = params.set('pageSize', pageSize);
    params = params.set('orderByDescending', orderByDescending);
    params = params.set('orderBy', orderBy);
    return this.http.get<OperationResultWithValue<PaginatedList<ServiceProviderSearchEntity>>>(`${this.baseUrl}/search`, { params });
  }

  deleteServiceProvider(id: string) {
    return this.http.put<OperationResult>(`${this.baseUrl}/delete/${id}`, {});
  }

  addServiceProviders(dto: ServiceProviderAddRequest) {
    return this.http.post<OperationResult>(`${this.baseUrl}/add`, dto);
  }

  generateSample(percentage: number) {
    return this.http.post<OperationResult>(`${this.baseUrl}/generate-sample?percentage=${percentage}`, {});
  }

  sendInstructionalEmail(userId = '') {
    return this.http.put<OperationResultWithValue<Date>>(`${this.baseUrl}/sample-providers/email?userId=${userId}`, {});
  }

  getEipsesSampleServiceProviders(
    eipsesSampleId: string,
    searchParams: any,
    pageNumber: number,
    pageSize: number,
    orderBy = 'lastname',
    orderByDescending = true
  ) {
    let params = new HttpParams();

    Object.keys(searchParams).forEach((k) => {
      params = params.set(k, searchParams[k]);
    });

    params = params.set('pageNumber', pageNumber);
    params = params.set('pageSize', pageSize);
    params = params.set('orderByDescending', orderByDescending);
    params = params.set('orderBy', orderBy);
    return this.http.get<OperationResultWithValue<PaginatedList<EipsesAeaServiceProvider>>>(
      `${this.baseUrl}/sample-providers/${eipsesSampleId}`,
      { params }
    );
  }

  getReplacementProvider(eipsesSampleId: string, aeaId: string) {
    return this.http.get<OperationResultWithValue<EipsesAeaServiceProvider>>(`${this.baseUrl}/sample-providers/${eipsesSampleId}/${aeaId}`);
  }

  getExport(eipsesSampleId: string, format: string, aeaId = '') {
    return this.http.get(`${this.baseUrl}/export/${eipsesSampleId}/${format}?aeaId=${aeaId}`, {
      responseType: 'blob',
    });
  }

  replaceSampleProvider(oldProviderId: string, newProviderId: string) {
    return this.http.put<OperationResultWithValue<Date>>(`${this.baseUrl}/sample-providers/replace/${oldProviderId}/${newProviderId}`, {});
  }

  nudgeAeaAdmins() {
    return this.http.post<OperationResult>(`${this.baseUrl}/aea-verification-nudge`, {});
  }

  getAllSamples() {
    return this.http.get<OperationResultWithValue<EipsesSample[]>>(`${this.baseUrl}/samples`);
  }

  sendSurveys(eipsesSampleId: string) {
    return this.http.post<OperationResultWithValue<Date>>(`${this.baseUrl}/surveys/${eipsesSampleId}`, {});
  }

  getSurveyData(eipsesSampleId: string, aeaIds: string[]) {
    let params = new HttpParams();
    aeaIds?.forEach((x) => (params = params.append('aeaIds', x)));
    return this.http.get<OperationResultWithValue<EipsesSurveyReport>>(`${this.baseUrl}/survey-data/${eipsesSampleId}`, { params });
  }

  lockLearnerSurveys(surveyId: string) {
    return this.http.put<OperationResultWithValue<Date>>(`${this.baseUrl}/lock-learner/${surveyId}`, {});
  }

  getSummaryDataReport(eipsesSampleId: string, filters: EipsesSurveyReportFilters) {
    return this.http.post(`${this.baseUrl}/summary-data/${eipsesSampleId}`, filters, {
      responseType: 'blob',
    });
  }
  getSurveyResponsesReport(eipsesSampleId: string, filters: any) {
    return this.http.post(`${this.baseUrl}/survey-responses/${eipsesSampleId}`, filters, {
      responseType: 'blob',
    });
  }
  getRawDataReport(eipsesSampleId: string, filters: any) {
    return this.http.post(`${this.baseUrl}/raw-data/${eipsesSampleId}`, filters, {
      responseType: 'blob',
    });
  }

  handleError(errorTitle: string, error: any): void {
    let errorMessage = '';
    if (error && error.error && error.error.errors) {
      errorMessage = Array.isArray(error.error.errors)
        ? error.error.errors.map((err) => err.description)?.join(',')
        : Object.keys(error.error.errors)
            .map((key) => error.error.errors[key])
            ?.join(',');
    } else if (error && error.errors) {
      errorMessage = Array.isArray(error.errors)
        ? error.errors.map((err) => err.description)?.join(',')
        : Object.keys(error.errors)
            .map((key) => error.errors[key])
            ?.join(',');
    } else if (error && error.message) {
      errorMessage = error.message;
    } else {
      errorMessage = JSON.stringify(error);
    }

    this.notificationService.errorWithAction(errorTitle, 'Why?', () => {
      this.notificationService.alert(errorMessage, errorTitle);
    });
  }
}
